import { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import PropTypes from 'prop-types';
import {
  Alert,
  Snackbar,
  Box,
  IconButton,
  SvgIcon,
  Tooltip,
  AppBar,
  Toolbar,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  DialogContentText,
  Button,
  useTheme,
  Typography,
  CircularProgress,
} from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import {
  Edit as EditIcon,
  Save as SaveIcon,
  XCircle as XCircleIcon,
  Trash as TrashIcon,
} from 'react-feather';
import PDFPreviewer from 'src/views/transmit/DataView/PDFPreviewer';
import { PictureAsPdf as PDFIcon } from '@mui/icons-material';

import { setMode } from 'src/actions/formPermissionsActions';
import useMediaQuery from '@mui/material/useMediaQuery';

const useStyles = makeStyles((theme) => ({
  root: {
    position: 'relative',
  },
  container: {
    boxShadow: theme.shadows[1],
    background: theme.palette.common.white,
    justifyContent: 'flex-end',
    borderBottomLeftRadius: '5px',
    borderBottomRightRadius: '5px',
    padding: '10px',
    width: 'fit-content',
    display: 'flex',
  },
  edit: {
    color: '#af1685',
    '&:hover': {
      color: '#e845bd', // 20% lighter on hover
      backgroundColor: '#f9fafb', // 2% lighter on hover
    },
  },
  delete: {
    color: '#b30000',
    '&:hover': {
      color: '#ff0000', // 20% lighter on hover
      backgroundColor: '#f9fafb', // 2% lighter on hover
    },
  },
  save: {
    color: '#28B3E5',
    '&:hover': {
      color: '#84d3f0', // 20% lighter on hover
      backgroundColor: '#f9fafb', // 2% lighter on hover
    },
  },
  viewNotes: {
    color: '#3E748D',
    '&:hover': {
      color: '#71a8c1', // 20% lighter on hover
      backgroundColor: '#f9fafb', // 2% lighter on hover
    },
  },
  iconButton: {
    padding: 0,
  },
}));

function Modes({
  resetForm,
  handleSubmit,
  onlySave,
  canDelete,
  handleDelete,
  isOrgProfilePage,
  orgData,
}) {
  const classes = useStyles();
  const dispatch = useDispatch();
  const [openDelete, setOpenDelete] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [snackbarOpen, setSnackbarOpen] = useState(false);

  const theme = useTheme();
  const mobileView = useMediaQuery(theme.breakpoints.down('lg'));

  const { mode } = useSelector((state) => state.formPermissions);

  const isEditMode = mode === 'edit';
  const isViewMode = mode === 'view';

  const structureDelete = () => {
    setOpenDelete(false);
    dispatch(setMode('view'));
    handleDelete();
  };
  const snackBar = (snackbarOpen) => {
    <Snackbar
      open={snackbarOpen}
      onClose={() => setSnackbarOpen(false)}
      anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
      autoHideDuration={6000}
    >
      <Alert
        onClose={() => setSnackbarOpen(false)}
        severity="success"
        sx={{ width: '50%' }}
      >
        To view the PDF, please ensure that pop-ups are enabled in your browser
        or that pop-up blocking is disabled.
      </Alert>
    </Snackbar>;
  };

  const getSelectedData = (orgData) => {
    if (
      !orgData.organizationData.organization.id ||
      !orgData.organizationData.organization.name
    )
      return [];
    return [
      {
        type: 'PAGE',
        label: `Referral Organization - ${orgData.organizationData.organization.name}`,
        value: {
          label: 'Organization Data',
          formId: 'organizationData',
          pageId: 'organizationData',
          orgData: {
            id: orgData.organizationData.organization.id,
          },
          queryId: orgData.organizationData.organization.id,
        },
        isSelected: true,
        willSelect: false,
      },
    ];
  };

  const previewDialog = (downloading) => (
    <Dialog
      open={downloading}
      onClose={() => {}}
      disableEscapeKeyDown
    >
      <DialogTitle>
        <Typography style={{ fontWeight: 'bold' }}>
          Attachment Preview
        </Typography>
      </DialogTitle>
      <DialogContent dividers>
        <Box
          display="flex"
          gap="15px"
          alignItems="center"
          padding="20px"
        >
          <CircularProgress size={24} />
          <Typography>Please wait... Generating PDF...</Typography>
        </Box>
      </DialogContent>
    </Dialog>
  );

  const pdfIconButton = ({ downloading, setDownloading, classes, orgData }) => {
    return (
      <>
        <Tooltip
          title="Download PDF"
          aria-label="download-pdf"
        >
          <IconButton
            onClick={() => {
              setDownloading(true);
              setSnackbarOpen(true);
            }}
            className={classes.iconButton}
            size="large"
            disabled={!isViewMode || downloading || !orgData}
          >
            <SvgIcon
              fontSize="small"
              className={
                !isViewMode || downloading || !orgData
                  ? classes.disabledIcon
                  : classes.save
              }
            >
              <PDFIcon />
            </SvgIcon>
          </IconButton>
          {downloading && (
            <PDFPreviewer
              selectedData={getSelectedData(orgData)}
              onClose={() => {}}
              showHeaders={true}
              sending={false}
              setSending={() => {}}
              downloading={downloading}
              setDownloading={setDownloading}
              setDisabled={() => {}}
              autoDownload={true}
            />
          )}
          {previewDialog(downloading)}
        </Tooltip>
      </>
    );
  };

  const confirmDelete = (
    <Dialog
      open={openDelete}
      onClose={() => setOpenDelete(false)}
      aria-labelledby="alert-dialog-title"
      aria-describedby="alert-dialog-description"
    >
      <DialogTitle id="alert-dialog-title">
        Are you sure you want to delete?
      </DialogTitle>
      <DialogContent>
        <DialogContentText id="alert-dialog-description">
          Once an item is deleted, it can not be returned.
        </DialogContentText>
      </DialogContent>
      <DialogActions>
        <Button
          onClick={() => setOpenDelete(false)}
          color="primary"
        >
          No
        </Button>
        <Button
          onClick={() => structureDelete()}
          color="primary"
          autoFocus
        >
          Yes
        </Button>
      </DialogActions>
    </Dialog>
  );

  return (
    <AppBar
      elevation={0}
      color="transparent"
      position="sticky"
    >
      <Toolbar
        style={{ justifyContent: 'flex-end', minHeight: 'initial' }}
        disableGutters
        variant="dense"
      >
        <Box className={classes.container}>
          {onlySave ? (
            <Box>
              <Tooltip
                title="Save"
                aria-label="save"
              >
                <IconButton
                  onClick={() => handleSubmit()}
                  className={classes.iconButton}
                  size="large"
                >
                  <SvgIcon
                    fontSize="small"
                    className={classes.save}
                  >
                    <SaveIcon />
                  </SvgIcon>
                </IconButton>
              </Tooltip>
            </Box>
          ) : (
            <>
              {isViewMode && (
                <Box
                  ml={0.5}
                  display="flex"
                  alignItems="center"
                  gap={2}
                >
                  {isOrgProfilePage &&
                    pdfIconButton({
                      downloading,
                      setDownloading,
                      classes,
                      orgData,
                    })}
                  {snackBar(snackbarOpen)}
                  <Tooltip
                    title="Edit"
                    aria-label="edit"
                  >
                    <IconButton
                      className={classes.iconButton}
                      onClick={() => dispatch(setMode('edit'))}
                      size="large"
                    >
                      <SvgIcon
                        fontSize="small"
                        className={classes.edit}
                      >
                        <EditIcon />
                      </SvgIcon>
                    </IconButton>
                  </Tooltip>
                </Box>
              )}
              {isEditMode && (
                <>
                  <Box mr={mobileView ? 5 : 1}>
                    <Tooltip
                      title="Save"
                      aria-label="save"
                    >
                      <IconButton
                        onClick={() => handleSubmit()}
                        className={classes.iconButton}
                        size="large"
                      >
                        <SvgIcon
                          fontSize="small"
                          className={classes.save}
                        >
                          <SaveIcon />
                        </SvgIcon>
                      </IconButton>
                    </Tooltip>
                  </Box>
                  {canDelete && (
                    <Box
                      Box
                      mr={1}
                    >
                      <Tooltip
                        title="Delete"
                        aria-label="delete"
                      >
                        <IconButton
                          className={classes.iconButton}
                          onClick={() => {
                            setOpenDelete(true);
                          }}
                          size="large"
                        >
                          <SvgIcon
                            fontSize="small"
                            className={classes.delete}
                          >
                            <TrashIcon />
                          </SvgIcon>
                        </IconButton>
                      </Tooltip>
                    </Box>
                  )}
                  <Box>
                    <Tooltip
                      title="Cancel"
                      aria-label="cancel"
                    >
                      <IconButton
                        className={classes.iconButton}
                        onClick={() => {
                          resetForm();
                          dispatch(setMode('view'));
                        }}
                        size="large"
                      >
                        <SvgIcon
                          fontSize="small"
                          className={classes.edit}
                        >
                          <XCircleIcon />
                        </SvgIcon>
                      </IconButton>
                    </Tooltip>
                  </Box>
                </>
              )}
            </>
          )}
        </Box>
      </Toolbar>
      {canDelete && confirmDelete}
    </AppBar>
  );
}

Modes.propTypes = {
  resetForm: PropTypes.func,
  handleDelete: PropTypes.func,
  handleSubmit: PropTypes.func,
  onlySave: PropTypes.bool,
  canDelete: PropTypes.bool,
  isOrgProfilePage: PropTypes.bool,
  orgData: PropTypes.object,
};

export default Modes;
